import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  MessageSquare,
  MessagesSquare,
  Mail,
  CheckCircle2,
  ArrowRight,
  Play,
  UserRound,
  Brain,
  Workflow,
  BarChart3,
  AudioLines,
  BookOpen,
  DollarSign,
  Sparkles,
  Send,
  Headphones,
  HeartPulse,
  PieChart,
  Droplet,
  ShieldCheck,
  BadgeCheck,
  Lock,
  ScrollText,
  Quote,
} from 'lucide-react';
import freshroadLogo from '../../../images/freshroadlogo.png';
import { Breadcrumb, CtaBand } from './DemoSiteLayout';
import SampleCall from './SampleCall';
import { SOLUTIONS } from './solutionsData';
import { T, SURFACE, HAIRLINE, SHADOW, Hero, PrimaryButton, GhostButton, DemoLink, Band, IconTile, CenterHead } from './siteUI';

/* =========================================================
   Solutions — what FreshRoad does, and who it is for.

   Sits between the landing page (which introduces the three products) and the
   product pages (which each sell one). This page carries the argument: most AI
   stops at conversation, and here are the platform capabilities behind
   actually finishing the work.
   ========================================================= */

const HERO_CHANNELS = [
  { name: 'Voice', icon: Phone, tint: 'bg-teal-50 text-teal-600' },
  { name: 'SMS', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600' },
  { name: 'Chat', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600' },
  { name: 'Email', icon: Mail, tint: 'bg-orange-50 text-orange-500' },
];

/* Wordmarks, not image assets — these render as styled text until real,
   permissioned logo files are dropped in. */
const CUSTOMER_LOGOS = [
  { name: 'FQHC', weight: 'font-serif font-bold tracking-tight' },
  { name: 'CarePoint Health', weight: 'font-bold tracking-tight' },
  { name: 'BloodSource', weight: 'font-semibold italic tracking-tight' },
  { name: 'Quorum Health', weight: 'font-semibold tracking-tight' },
  { name: 'HealthPlus Federal', weight: 'font-serif font-bold tracking-tight' },
  { name: 'Sunrise Physicians', weight: 'font-semibold tracking-tight' },
];

const VALUE_CHAIN = [
  { name: 'Customer', sub: 'Starts conversation', icon: UserRound, tint: 'bg-teal-50 text-teal-600' },
  { name: 'AI understands', sub: 'Intent', icon: Brain, tint: 'bg-violet-50 text-violet-600' },
  { name: 'AI completes', sub: 'The work', icon: Workflow, tint: 'bg-sky-50 text-sky-600' },
  { name: 'Business gets', sub: 'Better', icon: BarChart3, tint: 'bg-emerald-50 text-emerald-600' },
];

const PLATFORM_PILLARS = [
  { name: 'Omnichannel communication', sub: 'Voice, SMS, chat, email', icon: AudioLines, tint: 'bg-teal-50 text-teal-600' },
  { name: 'Enterprise knowledge', sub: 'Always up to date', icon: BookOpen, tint: 'bg-violet-50 text-violet-600' },
  { name: 'Workflow automation', sub: 'Execute and integrate', icon: Workflow, tint: 'bg-sky-50 text-sky-600' },
  { name: 'Customer intelligence', sub: 'Unified customer record', icon: UserRound, tint: 'bg-orange-50 text-orange-500' },
  { name: 'Analytics & insights', sub: 'Improve every day', icon: BarChart3, tint: 'bg-emerald-50 text-emerald-600' },
];

const TRADITIONAL_COSTS = [
  'Human agent salary & benefits',
  'Training and management',
  'Infrastructure & systems',
  '24/7 staffing requirements',
];

const FRESHROAD_COSTS = [
  'AI service usage',
  'Automatic updates & maintenance',
  'Cloud infrastructure included',
  '24/7 always available',
];

const HOW_STEPS = [
  { n: 1, name: 'Create Customer AI', desc: 'Build your AI agent in minutes with no code.', icon: Sparkles, tint: 'bg-teal-50 text-teal-600' },
  { n: 2, name: 'Connect knowledge & tools', desc: 'Integrate knowledge bases, systems, and workflows.', icon: BookOpen, tint: 'bg-violet-50 text-violet-600' },
  { n: 3, name: 'Deploy to all channels', desc: 'Go live across voice, SMS, chat, and email.', icon: Send, tint: 'bg-sky-50 text-sky-600' },
  { n: 4, name: 'Operate & optimize', desc: 'Monitor performance and continuously improve.', icon: BarChart3, tint: 'bg-orange-50 text-orange-500' },
];

const INDUSTRIES = [
  { name: 'Customer Service', desc: 'Automate support and resolve issues faster.', icon: Headphones, tint: 'bg-teal-50 text-teal-600', wash: 'from-teal-100/70 to-teal-50/30' },
  { name: 'Healthcare', desc: 'Improve patient access and reduce administrative burden.', icon: HeartPulse, tint: 'bg-rose-50 text-rose-500', wash: 'from-rose-100/70 to-rose-50/30' },
  { name: 'Research (CATI)', desc: 'Conduct surveys and collect data, more efficiently.', icon: PieChart, tint: 'bg-sky-50 text-sky-600', wash: 'from-sky-100/70 to-sky-50/30' },
  { name: 'Blood Banks', desc: 'Engage donors and streamline communication.', icon: Droplet, tint: 'bg-violet-50 text-violet-600', wash: 'from-violet-100/70 to-violet-50/30' },
];

/* Only quotes we have been given. Add entries here and the pager appears
   automatically — do not add attributed quotes without written sign-off. */
const RESULTS = [
  {
    org: 'Western Sierra Health Clinic',
    program: 'AI Patient Access Agent',
    metrics: [
      { label: 'Colorectal cancer screening (before)', value: '23%' },
      { label: 'Colorectal cancer screening (after)', value: '33.8%', highlight: true },
      { label: 'Patient satisfaction', value: '89.4%' },
    ],
    quote:
      'Fresh Road Customer AI has transformed how we operate. It’s like adding a team of AI experts that work 24/7.',
    person: 'Jessica Martinez',
    role: 'Operations Director, HealthPlus Federal',
  },
];

const SECURITY = [
  { name: 'HIPAA', sub: 'Compliant', icon: ShieldCheck },
  { name: 'SOC 2', sub: 'Type II compliant', icon: BadgeCheck },
  { name: 'ISO 27001', sub: 'Certified', icon: Lock },
  { name: 'Enterprise security', sub: 'End-to-end data protection', icon: ShieldCheck },
  { name: 'Audit logs', sub: 'Full visibility & compliance', icon: ScrollText },
];

/* ---------- hero diagram ---------- */

const ChannelChip = ({ name, icon: Icon, tint }) => (
  <div className={`flex w-[76px] flex-col items-center gap-1.5 px-2 py-2.5 ${SURFACE.card}`}>
    <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tint}`}>
      <Icon size={15} strokeWidth={1.9} />
    </span>
    <span className="text-[11px] font-semibold leading-none">{name}</span>
  </div>
);

const HeroDiagram = () => (
  <div className="flex w-full items-center justify-center gap-3 sm:gap-4">
    <div className="flex shrink-0 flex-col gap-2.5">
      {HERO_CHANNELS.map((c) => (
        <ChannelChip key={c.name} {...c} />
      ))}
    </div>

    <div className="h-px min-w-[14px] flex-1 border-t-2 border-dashed border-slate-300" />

    {/* Signature hub, matching the Customer AI Platform page. */}
    <div className="relative flex h-[136px] w-[136px] shrink-0 flex-col items-center justify-center rounded-full bg-white ring-1 ring-slate-900/5 shadow-[0_0_0_12px_rgba(20,184,166,0.07),0_0_0_26px_rgba(20,184,166,0.035),0_2px_4px_rgba(15,23,42,0.05),0_16px_40px_-16px_rgba(15,23,42,0.2)]">
      <img src={freshroadLogo} alt="" className="h-8 w-8 object-contain" />
      <p className="mt-1 text-center text-[12px] font-bold leading-tight">FreshRoad</p>
      <p className="text-center text-[11px] font-medium text-teal-600">Customer AI</p>
    </div>

    <div className="h-px min-w-[14px] flex-1 border-t-2 border-dashed border-slate-300" />

    <div className={`flex w-[88px] shrink-0 flex-col items-center gap-2 px-2 py-3.5 ${SURFACE.card}`}>
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
        <CheckCircle2 size={18} strokeWidth={1.9} />
      </span>
      <span className="text-center text-[11px] font-semibold leading-tight">
        Completed
        <br />
        work
      </span>
    </div>
  </div>
);

/* ---------- results ---------- */

const Results = () => {
  const [i, setI] = useState(0);
  const r = RESULTS[i];

  return (
    <section className={`px-6 py-9 md:px-10 ${SURFACE.band}`}>
      <CenterHead title="Real results. Real impact." />

      <div className="mt-7 grid grid-cols-1 gap-8 lg:grid-cols-[1.35fr_1fr]">
        <div>
          <p className="text-[14px] font-semibold">{r.org}</p>
          <p className={T.micro}>{r.program}</p>

          <div className="mt-5 flex flex-wrap items-end gap-x-6 gap-y-4">
            {r.metrics.map((m, idx) => (
              <React.Fragment key={m.label}>
                {idx === 1 && <ArrowRight size={16} className="mb-4 shrink-0 text-slate-300" />}
                <div className="min-w-[120px]">
                  <p
                    className={`text-[30px] font-bold leading-none tracking-[-0.02em] ${
                      m.highlight ? 'text-teal-600' : 'text-slate-800'
                    }`}
                  >
                    {m.value}
                  </p>
                  <p className={`mt-1.5 max-w-[16ch] ${T.micro}`}>{m.label}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        <figure className={`flex flex-col justify-center rounded-2xl bg-slate-50/60 p-6 ${HAIRLINE}`}>
          <Quote size={20} className="shrink-0 text-teal-500" />
          <blockquote className={`mt-3 max-w-[46ch] ${T.body}`}>{r.quote}</blockquote>
          <figcaption className="mt-4 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-100 text-[11px] font-bold text-teal-700">
              {r.person.split(' ').map((p) => p[0]).join('').slice(0, 2)}
            </span>
            <span>
              <span className="block text-[13px] font-semibold">{r.person}</span>
              <span className={`block ${T.micro}`}>{r.role}</span>
            </span>
          </figcaption>
        </figure>
      </div>

      {/* Pager only renders once there is more than one approved story. */}
      {RESULTS.length > 1 && (
        <div className="mt-7 flex justify-center gap-2">
          {RESULTS.map((item, idx) => (
            <button
              key={item.org}
              onClick={() => setI(idx)}
              aria-label={`Show results from ${item.org}`}
              aria-current={idx === i}
              className={`h-2 w-2 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                idx === i ? 'bg-teal-600' : 'bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

/* ---------- page ---------- */

const DemoSiteSolutions = () => (
  <>
    <Breadcrumb trail={['Home', 'Solutions']} />

    {/* Hero */}
    <section className="grid grid-cols-1 items-center gap-12 py-8 lg:grid-cols-[1.05fr_1fr]">
      <Hero
        eyebrow="AI customer operations platform"
        title={
          <>
            Turn every customer conversation into{' '}
            <span className="text-teal-600">completed work.</span>
          </>
        }
        actions={
          <>
            <DemoLink />
            <SampleCall
              src="/audio/conversational-ai.mp3"
              label="Hear a real call"
              caption="Inbound enquiry, answered and resolved end to end"
            />
          </>
        }
      >
        AI Customer Operations across voice, SMS, chat, and email. Resolve issues, complete tasks,
        and deliver exceptional experiences — at scale.
      </Hero>

      <HeroDiagram />
    </section>

    {/* Customer logos */}
    <section className="py-6">
      <p className={`text-center ${T.micro}`}>Trusted by organizations of all sizes</p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
        {CUSTOMER_LOGOS.map(({ name, weight }) => (
          <span key={name} className={`text-[15px] text-slate-600 ${weight}`}>
            {name}
          </span>
        ))}
      </div>
    </section>

    {/* Completes the work */}
    <section className={`px-6 py-10 md:px-10 ${SURFACE.band}`}>
      <CenterHead
        title={
          <>
            Most AI stops at conversation.{' '}
            <span className="text-teal-600">Fresh Road completes the work.</span>
          </>
        }
      />

      {/* A sequence, and a thin row — numbered rather than tiled, so it does
          not compete with the capability cards directly beneath it. */}
      <ol className="mx-auto mt-8 flex max-w-[900px] flex-wrap items-start justify-center gap-x-4 gap-y-5">
        {VALUE_CHAIN.map(({ name, sub }, i) => (
          <React.Fragment key={name}>
            {i > 0 && <ArrowRight size={15} className="mt-1 hidden shrink-0 text-slate-300 sm:block" />}
            <li className="flex w-[160px] flex-col text-center">
              <span className="text-[12px] font-bold text-teal-600 tabular-nums">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="mt-1 text-[13.5px] font-semibold leading-tight">{name}</span>
              <span className={`mt-0.5 ${T.micro}`}>{sub}</span>
            </li>
          </React.Fragment>
        ))}
      </ol>

      <p className={`mt-12 text-center ${T.label}`}>One platform. Everything works together.</p>
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {PLATFORM_PILLARS.map(({ name, sub, icon: Icon, tint }) => (
          <div key={name} className={`flex flex-col p-5 ${SURFACE.cardHover}`}>
            <IconTile icon={Icon} tint={tint} />
            <p className="mt-3.5 text-[14px] font-semibold leading-snug">{name}</p>
            <p className={`mt-1.5 ${T.micro}`}>{sub}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Cost savings */}
    <Band
      title="Reduce customer operations costs by up to 94%"
      subtitle="Replace repetitive customer service work with AI that operates 24/7."
      centered
    >
      <div className="mx-auto mt-9 grid max-w-[820px] grid-cols-1 items-center gap-4 md:grid-cols-[1fr_auto_1fr]">
        <div className="rounded-2xl bg-slate-50/60 p-6 ring-1 ring-slate-900/5">
          <p className="text-center text-[13.5px] font-semibold text-slate-600">
            Traditional customer operations
          </p>
          <p className="mt-3 text-center text-[34px] font-bold tracking-[-0.025em] text-slate-400">
            $150,000
          </p>
          <p className={`text-center ${T.micro}`}>For 10,000 hours annually</p>
          <ul className="mt-5 space-y-2.5">
            {TRADITIONAL_COSTS.map((c) => (
              <li key={c} className="flex items-start gap-2.5 text-[13px] text-slate-500">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-slate-300" />
                {c}
              </li>
            ))}
          </ul>
        </div>

        <span className="mx-auto text-[13px] font-bold text-slate-400">VS</span>

        <div className={`relative rounded-2xl bg-teal-50/50 p-6 ring-2 ring-teal-500/70 ${SHADOW.raised}`}>
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-teal-600 px-3 py-1 text-[11px] font-bold text-white">
            Best value
          </span>
          <p className="text-center text-[13.5px] font-semibold text-teal-700">FreshRoad Customer AI</p>
          <p className="mt-3 text-center text-[34px] font-bold tracking-[-0.025em] text-teal-700">$9,000</p>
          <p className={`text-center ${T.micro}`}>For 10,000 hours annually</p>
          <ul className="mt-5 space-y-2.5">
            {FRESHROAD_COSTS.map((c) => (
              <li key={c} className="flex items-start gap-2.5 text-[13px] text-slate-600">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-teal-500" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-7 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-5 py-2 text-[13px] font-bold text-white">
          <DollarSign size={14} /> 94% cost reduction
        </span>
      </div>
    </Band>

    {/* How it works */}
    <Band title="How Fresh Road works" subtitle="Four steps from an empty account to an agent handling live volume." centered>
      <div className="mt-9 flex flex-wrap items-start justify-center gap-3 md:gap-4">
        {HOW_STEPS.map(({ n, name, desc, icon: Icon, tint }, i) => (
          <React.Fragment key={n}>
            {i > 0 && <ArrowRight size={16} className="mt-8 hidden shrink-0 text-slate-300 md:block" />}
            <div className="flex w-[176px] flex-col items-center text-center">
              <span className="relative">
                <IconTile icon={Icon} tint={tint} size="lg" />
                <span className="absolute -left-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-[11px] font-bold text-white ring-2 ring-white">
                  {n}
                </span>
              </span>
              <p className="mt-3.5 text-[13.5px] font-semibold leading-tight">{name}</p>
              <p className={`mt-1.5 ${T.micro}`}>{desc}</p>
            </div>
          </React.Fragment>
        ))}
      </div>
    </Band>

    {/* Industries */}
    <section className="px-2 py-8">
      <CenterHead
        title="Built for your industry"
        subtitle="Where Customer AI is already carrying volume."
      />
      <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {INDUSTRIES.map(({ name, desc, icon: Icon, tint, wash }) => (
          <div key={name} className={`flex flex-col overflow-hidden ${SURFACE.cardHover}`}>
            {/* Tinted wash stands in for photography until real assets exist. */}
            <div className={`flex h-[104px] items-center justify-center bg-gradient-to-br ${wash}`}>
              <span className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-white/90 ${tint}`}>
                <Icon size={22} strokeWidth={1.85} />
              </span>
            </div>
            <div className="flex flex-1 flex-col p-5">
              <p className="text-[14px] font-semibold">{name}</p>
              <p className={`mt-1.5 flex-1 ${T.micro}`}>{desc}</p>
              <Link
                to="/site/customer-ai-platform"
                aria-label={`Explore ${name} solutions`}
                className="mt-4 w-fit rounded-lg text-teal-600 transition-colors hover:text-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
              >
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        ))}
      </div>
      {/* The solutions themselves, in the same card treatment as the
          industries above so the two grids read as one set. Generated from
          solutionsData — a new entry there appears here automatically. */}
      <div className="mt-12">
        <CenterHead
          title="Solutions"
          subtitle="Four ways teams put FreshRoad to work. Same platform, configured for a different job."
        />
        <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SOLUTIONS.map(({ slug, name, tagline, icon: Icon, tint, cardWash }) => (
            <Link
              key={slug}
              to={`/site/solutions/${slug}`}
              className={`flex flex-col overflow-hidden ${SURFACE.cardHover} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600`}
            >
              <span className={`flex h-[104px] items-center justify-center bg-gradient-to-br ${cardWash}`}>
                <span className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-white/90 ${tint}`}>
                  <Icon size={22} strokeWidth={1.85} />
                </span>
              </span>
              <span className="flex flex-1 flex-col p-5">
                <span className="text-[14px] font-semibold">{name}</span>
                <span className={`mt-1.5 flex-1 ${T.micro}`}>{tagline}</span>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-teal-700">
                  Learn more <ArrowRight size={14} />
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    <Results />

    {/* Security */}
    <Band
      title="Enterprise-grade security you can trust"
      subtitle="Audited controls for regulated operations — healthcare, research and financial services."
      centered
    >
      <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
        {SECURITY.map(({ name, sub, icon: Icon }) => (
          <div key={name} className="flex flex-col items-center text-center">
            <span className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-teal-600 ${HAIRLINE} ${SHADOW.card}`}>
              <Icon size={21} strokeWidth={1.8} />
            </span>
            <p className="mt-3 text-[13px] font-bold">{name}</p>
            <p className={`mt-0.5 max-w-[20ch] ${T.micro}`}>{sub}</p>
          </div>
        ))}
      </div>
    </Band>

    <CtaBand
      title="Ready to turn conversations into results?"
      subtitle="See how Fresh Road Customer AI can transform your customer operations."
      primary="Book a demo"
    />
  </>
);

export default DemoSiteSolutions;
